// Per-page title, description and canonical for the client-rendered public
// pages on raisedbeef.com.
//
// WHY THIS EXISTS: every URL in this SPA is answered by index.html, whose
// <title> is "RaisedBeef Market Intelligence" and whose og:url is
// https://markets.raisedbeef.ai/. So /sales, /leaderboard, /platform and the
// rest all shared one title and all told Google their canonical URL was on a
// DIFFERENT DOMAIN. That is not a missing nicety — a wrong canonical is an
// instruction to index something else, and it applied to the whole domain.
//
// This is a floor, not a ceiling. These pages are still client-rendered, so a
// crawler has to execute JavaScript to see their content at all. The homepage
// solves that properly by being prerendered (see RbGateHome + rb-home.html);
// prerendering these too is the follow-up.

import { useEffect } from "react";
import { CMC_HOSTS } from "@/brand/brand";

import { isRaisedBeefHomeHost } from "@/lib/siteHost";

export interface SeoInput {
  /** Full <title>. Written out per page rather than templated — a page's title
   *  is the single biggest on-page ranking signal and deserves the words. */
  title: string;
  description?: string;
  /** Path this page should declare as canonical, e.g. "/sales". Defaults to
   *  the current path. Pass explicitly on any route with query params or
   *  aliases, so the variants collapse to one URL. */
  path?: string;
  /** Keep this page out of search results. For admin and account surfaces —
   *  pages that are reachable but have no business being indexed. */
  noindex?: boolean;
}

/** raisedbeef.com is served on both the apex and www; www is the canonical
 *  form (it is what robots.txt and the gate homepage already declare). */
function canonicalOrigin(): string {
  // Cattle Market Central is the public site; raisedbeef.com redirects to it except the barn pages
  // (owner, 2026-11-03), which keep their own address.
  if (CMC_HOSTS.includes(window.location.hostname)) return "https://cattlemarketcentral.com";
  if (isRaisedBeefHomeHost()) return "https://www.raisedbeef.com";
  return window.location.origin;
}

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSeo({ title, description, path, noindex }: SeoInput): void {
  useEffect(() => {
    document.title = title;
    setMeta('meta[property="og:title"]', "property", "og:title", title);

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
    }

    const url = `${canonicalOrigin()}${path ?? window.location.pathname}`;

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;

    setMeta('meta[property="og:url"]', "property", "og:url", url);

    // Always written, never merely omitted. These tags are global and survive
    // client-side navigation, so leaving the previous page's value in place
    // would carry a noindex off an admin page onto the next public page a
    // visitor clicks — silently de-indexing it.
    setMeta(
      'meta[name="robots"]', "name", "robots",
      noindex ? "noindex, nofollow" : "index, follow",
    );

    // No cleanup: these tags are global and every public page sets its own on
    // mount. Restoring index.html's markets.raisedbeef.ai defaults on unmount
    // would put the wrong canonical back mid-session.
  }, [title, description, path, noindex]);
}
